import api from './api';
import type { BlockLessonContent } from '../components/Courses/editors/BlockEditor/types';

/** Structure of a v2 course as returned by the reorder/move endpoints. */
export interface CourseStructure {
    courseId: number;
    sections: { id: number; title: string; position: number; lessonIds: number[] }[];
    lessons: { id: number; sortOrder: number; sectionId: number | null }[];
}

export interface LessonContentVersionSummary {
    id: number;
    lessonId: number;
    versionNumber: number;
    blockCount: number;
    source: 'autosave' | 'manual' | 'pre_restore';
    comment: string | null;
    createdBy: number | null;
    createdAt: string;
}

export interface LessonContentVersionDetail extends LessonContentVersionSummary {
    content: BlockLessonContent;
}

/**
 * Autorentool v2 endpoints that are not plain API-Platform resources:
 * section/lesson ordering (global sortOrder is rewritten server-side) and the
 * version history of block lessons.
 */
export const courseStructureService = {
    async reorderSections(courseId: number, orderedIds: number[]): Promise<CourseStructure> {
        const { data } = await api.put(`/courses/${courseId}/sections/reorder`, { orderedIds });
        return data;
    },

    async reorderLessons(sectionId: number, orderedIds: number[]): Promise<CourseStructure> {
        const { data } = await api.put(`/course_sections/${sectionId}/lessons/reorder`, { orderedIds });
        return data;
    },

    async moveLesson(lessonId: number, targetSectionId: number, position: number): Promise<CourseStructure> {
        const { data } = await api.put(`/lessons/${lessonId}/move`, { targetSectionId, position });
        return data;
    },

    async listVersions(lessonId: number): Promise<LessonContentVersionSummary[]> {
        const { data } = await api.get(`/lessons/${lessonId}/content-versions`);
        return data['hydra:member'] ?? data.member ?? [];
    },

    /** History plus the server's retention limit, so the UI can explain why old versions are gone (#526). */
    async getVersionHistory(lessonId: number): Promise<{ versions: LessonContentVersionSummary[]; maxVersions: number | null }> {
        const { data } = await api.get(`/lessons/${lessonId}/content-versions`);
        return { versions: data['hydra:member'] ?? data.member ?? [], maxVersions: typeof data.maxVersions === 'number' ? data.maxVersions : null };
    },

    /** Named snapshot of the current content (#526). */
    async createVersion(lessonId: number, comment: string): Promise<LessonContentVersionSummary> {
        const { data } = await api.post(`/lessons/${lessonId}/content-versions`, { comment });
        return data;
    },

    async getVersion(lessonId: number, versionNumber: number): Promise<LessonContentVersionDetail> {
        const { data } = await api.get(`/lessons/${lessonId}/content-versions/${versionNumber}`);
        return data;
    },

    async restoreVersion(lessonId: number, versionNumber: number): Promise<{ id: number; content: BlockLessonContent; restoredFrom: number }> {
        const { data } = await api.post(`/lessons/${lessonId}/content-versions/${versionNumber}/restore`);
        return data;
    },
};

export default courseStructureService;
